import React from 'react';
import { useSelector } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getPostBySlug, getRelatedPosts, formatPostDate, AUTHOR_NAME } from '../../blog';
import renderMarkdown from '../PageBuilder/markdownProcessor';
import { Link as MarkdownLink } from '../PageBuilder/Primitives/Link';

import { Page, LayoutSingleColumn, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import css from './BlogPostPage.module.css';

// Markdown is rendered into our own elements so the article inherits the
// Nextmine type scale, and so in-app links stay client side navigations.
const markdownComponents = {
  h1: props => <h2 className={css.h2} {...props} />,
  h2: props => <h2 className={css.h2} {...props} />,
  h3: props => <h3 className={css.h3} {...props} />,
  p: props => <p className={css.p} {...props} />,
  ul: props => <ul className={css.ul} {...props} />,
  ol: props => <ol className={css.ol} {...props} />,
  li: props => <li className={css.li} {...props} />,
  blockquote: props => <blockquote className={css.blockquote} {...props} />,
  hr: props => <hr className={css.hr} {...props} />,
  a: props => <MarkdownLink className={css.link} {...props} />,
  // Tables scroll inside their own wrapper so a wide comparison never makes
  // the whole page scroll sideways on a phone.
  table: props => (
    <div className={css.tableWrap}>
      <table className={css.table} {...props} />
    </div>
  ),
  th: props => <th className={css.th} {...props} />,
  td: props => <td className={css.td} {...props} />,
};

/**
 * A single journal post.
 *
 * The body is markdown kept in src/blog/posts, rendered server side. The page
 * emits BlogPosting, FAQPage and BreadcrumbList structured data, and the FAQ
 * answers in the schema are exactly the ones rendered on the page.
 *
 * @param {Object} props
 * @param {Object} props.params route params, containing the post slug
 * @param {Object} props.staticContext StaticRouter context, needed for a real 404
 */
const BlogPostPage = props => {
  const { params, staticContext } = props;
  const intl = useIntl();
  const config = useConfiguration();
  const scrollingDisabled = useSelector(isScrollingDisabled);

  const post = getPostBySlug(params?.slug);

  if (!post) {
    return <NotFoundPage staticContext={staticContext} />;
  }

  const related = getRelatedPosts(post);
  const rootURL = config.marketplaceRootURL;
  const postURL = `${rootURL}/blog/${post.slug}`;
  const updated = post.updated || post.published;

  const publisher = {
    '@type': 'Organization',
    name: config.marketplaceName,
    url: rootURL,
  };
  const author = { '@type': 'Person', name: AUTHOR_NAME };

  const schema = [
    {
      '@type': 'BlogPosting',
      '@id': postURL,
      mainEntityOfPage: { '@type': 'WebPage', '@id': postURL },
      headline: post.title,
      description: post.description,
      url: postURL,
      datePublished: post.published,
      dateModified: updated,
      inLanguage: 'en',
      keywords: (post.keywords || []).join(', '),
      articleSection: post.topic,
      wordCount: post.body.split(/\s+/).filter(Boolean).length,
      image: post.image
        ? [
            {
              '@type': 'ImageObject',
              url: `${rootURL}${post.image.src}`,
              width: post.image.width,
              height: post.image.height,
            },
          ]
        : config.branding?.facebookImage
        ? [config.branding.facebookImage]
        : undefined,
      author,
      publisher,
      isPartOf: { '@type': 'Blog', '@id': `${rootURL}/blog` },
    },
    ...(post.faq?.length
      ? [
          {
            '@type': 'FAQPage',
            mainEntity: post.faq.map(item => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: { '@type': 'Answer', text: item.a },
            })),
          },
        ]
      : []),
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: config.marketplaceName, item: rootURL },
        { '@type': 'ListItem', position: 2, name: 'Journal', item: `${rootURL}/blog` },
        { '@type': 'ListItem', position: 3, name: post.title, item: postURL },
      ],
    },
  ];

  return (
    <Page
      title={post.title}
      description={post.description}
      scrollingDisabled={scrollingDisabled}
      schema={schema}
      openGraphType="article"
      published={post.published}
      updated={updated}
      author={AUTHOR_NAME}
      socialSharing={{
        title: post.title,
        description: post.description,
        images1200: post.image
          ? [
              {
                url: `${rootURL}${post.image.src}`,
                width: post.image.width,
                height: post.image.height,
              },
            ]
          : undefined,
      }}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <article className={css.root}>
          <nav className={css.breadcrumb} aria-label="Breadcrumb">
            <NamedLink name="BlogPage" className={css.breadcrumbLink}>
              Journal
            </NamedLink>
            <span className={css.dot}>·</span>
            <span>{post.topic}</span>
          </nav>

          <h1 className={css.title}>{post.title}</h1>

          <p className={css.meta}>
            <span className={css.byline}>By {AUTHOR_NAME}</span>
            <span className={css.dot}>·</span>
            <time dateTime={post.published}>{formatPostDate(post.published)}</time>
            <span className={css.dot}>·</span>
            <span>{post.readingMinutes} min read</span>
          </p>

          {post.image ? (
            <figure className={css.hero}>
              {/* width and height are set so the browser reserves the space and
                  the text below does not jump once the photograph loads. */}
              <img
                className={css.heroImage}
                src={post.image.src}
                width={post.image.width}
                height={post.image.height}
                alt={post.image.alt}
              />
              <figcaption className={css.heroCaption}>
                {post.image.caption || post.image.alt}
              </figcaption>
            </figure>
          ) : null}

          {post.summary?.length ? (
            <section className={css.summary} aria-label="In short">
              <h2 className={css.summaryHeading}>In short</h2>
              <ul className={css.summaryList}>
                {post.summary.map(point => (
                  <li key={point} className={css.summaryItem}>
                    {point}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <div className={css.body}>{renderMarkdown(post.body, markdownComponents)}</div>

          {post.faq?.length ? (
            <section className={css.faq}>
              <h2 className={css.h2}>Common questions</h2>
              {post.faq.map(item => (
                <details key={item.q} className={css.faqItem}>
                  <summary className={css.question}>{item.q}</summary>
                  <p className={css.answer}>{item.a}</p>
                </details>
              ))}
            </section>
          ) : null}

          <section className={css.cta}>
            <p className={css.ctaText}>
              Nextmine is a marketplace for jewelry that has already had one life. Buyers pay into
              escrow, sellers are paid once the piece has arrived, and every listing is someone's
              own photographs of their own piece.
            </p>
            <p className={css.ctaLinks}>
              <NamedLink name="SearchPage" className={css.link}>
                Browse the pieces for sale
              </NamedLink>
              <span className={css.dot}>·</span>
              <NamedLink name="HowPaymentsWorkPage" className={css.link}>
                How payments work
              </NamedLink>
              <span className={css.dot}>·</span>
              <NamedLink name="FaqPage" className={css.link}>
                Questions and answers
              </NamedLink>
            </p>
          </section>

          {related.length ? (
            <section className={css.related}>
              <h2 className={css.relatedHeading}>Keep reading</h2>
              <ul className={css.relatedList}>
                {related.map(item => (
                  <li key={item.slug} className={css.relatedItem}>
                    <NamedLink
                      name="BlogPostPage"
                      params={{ slug: item.slug }}
                      className={css.relatedLink}
                    >
                      {item.title}
                    </NamedLink>
                    <p className={css.relatedExcerpt}>{item.description}</p>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <p className={css.backLink}>
            <NamedLink name="BlogPage" className={css.link}>
              {intl.formatMessage({ id: 'BlogPostPage.backToIndex' })}
            </NamedLink>
          </p>
        </article>
      </LayoutSingleColumn>
    </Page>
  );
};

export default BlogPostPage;
